import React, { forwardRef } from "react";
import "./doctorCard.scss";
import Image from "next/image";
import Link from "next/link";
import Head from "next/head";
import { useIntl } from "react-intl";
import ForRating from "@/components/Rating/ForRating";

const DoctorCardWithApi = forwardRef(({ doctor }, ref) => {
  const intl = useIntl();

  const doctorData = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: doctor?.name,
    jobTitle: doctor?.Designation,
    alumniOf: doctor?.institute,
    description: doctor?.descrption,
    image: doctor?.profile?.url,
    gender: doctor?.gender,
    department: doctor?.Department,
    location: {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        addressCountry: doctor?.country,
        addressLocality: doctor?.city,
      },
    },
    worksFor: doctor?.Visiting_consultant_at,
  };

  return (
    <Link href={`/doctors/${doctor?.slug}`}>
      <div
        className="doctorCardWrapper hover:shadow-lg transition-all duration-300 w-full max-w-[280px] mx-auto h-auto min-h-[320px] flex flex-col"
        ref={ref}
      >
        <Head>
          <script type="application/ld+json">
            {JSON.stringify(doctorData)}
          </script>
        </Head>
        <div className="doctorImgWrapper w-[100px] h-[100px] relative mx-auto mb-3 flex-shrink-0">
          <Image
            loading="lazy"
            src={doctor?.profile?.url || "/images/default-doctor.png"}
            alt={doctor?.name || "Doctor Profile"}
            fill
            sizes="100px"
            className="object-cover rounded-full"
          />
        </div>
        <div className="flex-1 w-full px-3 overflow-hidden">
          <span className="flex justify-center mb-1 sm:mb-2 w-full">
            <ForRating
              initialValue={doctor?.rating || 4.6}
              allowFraction
              size={12}
              readonly
              className="flex flex-row"
            />
          </span>
          <div className="doctorInfo text-center w-full space-y-1">
            <p className="name text-base font-semibold text-gray-800 line-clamp-1">
              {doctor?.name || "\u00A0"}
            </p>
            {doctor?.Designation && (
              <p className="designation text-sm text-blue-600 font-medium line-clamp-1">
                {doctor?.Designation}
              </p>
            )}
            {doctor?.institute && (
              <p className="studiedAt text-xs text-gray-600 mb-2 line-clamp-1">
                {doctor?.institute}
              </p>
            )}
            <div className="stats grid grid-cols-2 gap-2 my-2">
              {doctor?.experiance && (
                <div className="stat text-center">
                  <p className="text-xs text-gray-500">Experience</p>
                  <p className="text-sm font-semibold text-gray-800">
                    {doctor?.experiance}+ years
                  </p>
                </div>
              )}
              {doctor?.surgeries && (
                <div className="stat text-center">
                  <p className="text-xs text-gray-500">Surgeries</p>
                  <p className="text-sm font-semibold text-gray-800">
                    {doctor?.surgeries}+
                  </p>
                </div>
              )}
            </div>
            {(doctor?.city || doctor?.country) && (
              <p className="location text-xs text-gray-500 line-clamp-1">
                📍 {[doctor?.city, doctor?.country].filter(Boolean).join(", ")}
              </p>
            )}
          </div>
        </div>

        <div className="priceInfo w-full px-3 pb-3 mt-auto">
          <div className="freeConsultBtn text-center w-full bg-blue-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
            Contact Doctor
          </div>
        </div>
      </div>
    </Link>
  );
});

DoctorCardWithApi.displayName = "DoctorCardWithApi";

export default DoctorCardWithApi;
